import { useMemo, useState } from "react";

import { omit } from "lodash-es";
import { twJoin } from "tailwind-merge";

import {
  getSchemaTypeIcon,
  isBaseSchemaType,
} from "src/base-components/SchemaTypeSelector";
import {
  ColumnsConfig,
  getAllKeys,
  getColumnDisplayName,
  getColumnDisplayType,
  isColumnEditable,
  isStringCell,
  MIN_STRING_CELL_HEIGHT,
  resolveColumnType,
  TABLE_COLUMN_MIN_WIDTH,
  TABLE_COLUMN_WIDTH,
} from "src/cases/caseView/blocks/cellTypes";
import { ReadModeStringCell } from "src/cases/caseView/blocks/MultiRecordDisplay";
import { TypedCellDisplay } from "src/cases/caseView/blocks/TypedCellDisplay";
import {
  alignTableRecords,
  getRowChangeKind,
  isCellChanged,
  TableRowChange,
} from "src/cases/caseView/history/events/tableBlockDiff";
import { TableBlockMultiColumnRecord } from "src/cases/queries/casesTypes";
import { TableEditMode } from "src/clients/case-management";
import { Icon } from "src/design-system/Icon";

type RecordDiffTableProps = {
  beforeRecord: TableBlockMultiColumnRecord;
  afterRecord: TableBlockMultiColumnRecord;
  columnsConfig: ColumnsConfig;
  defaultColumnsEditMode?: TableEditMode;
};

/** Which value a cell shows: editable columns split into a before/after
 *  pair, while read-only columns can't change and show one as-is value. */
type DiffSide = "before" | "after" | "readonly";

const EDITABLE_SIDES: DiffSide[] = ["before", "after"];
const READ_ONLY_SIDES: DiffSide[] = ["readonly"];

type DiffColumn = {
  key: string;
  sides: DiffSide[];
};

const SIDE_LABELS: Record<DiffSide, string | null> = {
  before: "Before",
  after: "After",
  readonly: null,
};

// Fixed column sizing like the table block, so long content scrolls or
// resizes inside its cell instead of stretching the column. Slightly wider
// than the block's columns so datetime values fit on one line.
const SUB_COLUMN_WIDTH = TABLE_COLUMN_WIDTH + 40;
const SUB_COLUMN_STYLE = {
  width: SUB_COLUMN_WIDTH,
  minWidth: TABLE_COLUMN_MIN_WIDTH,
  maxWidth: SUB_COLUMN_WIDTH,
} as const;

/** Per-cell drag-to-expand heights, as in the table block's read mode. */
const useCellHeights = () => {
  const [heights, setHeights] = useState<Record<string, number>>({});

  return {
    getHeight: (cellKey: string) => heights[cellKey],
    setHeight: (cellKey: string, height: number) =>
      setHeights((prev) => {
        // A single-line height means default rendering — drop the entry, the
        // rule the block's own resize state uses, so a cell dragged back down
        // truncates again instead of scrolling inside one line.
        if (height <= MIN_STRING_CELL_HEIGHT) {
          return cellKey in prev ? omit(prev, cellKey) : prev;
        }
        return prev[cellKey] === height ? prev : { ...prev, [cellKey]: height };
      }),
  };
};

export const RecordDiffTable: React.FC<RecordDiffTableProps> = ({
  beforeRecord,
  afterRecord,
  columnsConfig,
  defaultColumnsEditMode,
}) => {
  const { getHeight, setHeight } = useCellHeights();

  const columns: DiffColumn[] = useMemo(
    () =>
      getAllKeys([...afterRecord, ...beforeRecord]).map((key) => ({
        key,
        sides: isColumnEditable(key, columnsConfig, defaultColumnsEditMode)
          ? EDITABLE_SIDES
          : READ_ONLY_SIDES,
      })),
    [afterRecord, beforeRecord, columnsConfig, defaultColumnsEditMode],
  );

  // Row alignment is quadratic in row count (~250ms at 500 rows), and this
  // component re-renders on every resize frame, so it must not re-run.
  const rowChanges = useMemo(
    () => alignTableRecords(beforeRecord, afterRecord),
    [beforeRecord, afterRecord],
  );

  return (
    <div
      className="decideScrollbar overflow-x-auto"
      data-loc="table-changes-modal-table"
    >
      <table className="border-collapse">
        <thead>
          <tr className="bg-gray-50">
            {columns.map((column) => (
              <ColumnHeaderCell
                key={column.key}
                column={column}
                columnsConfig={columnsConfig}
              />
            ))}
          </tr>
          <tr>
            {columns.flatMap((column) =>
              column.sides.map((side) => (
                <SubHeaderCell key={`${column.key}-${side}`} side={side} />
              )),
            )}
          </tr>
        </thead>
        <tbody>
          {rowChanges.map((change, rowIndex) => (
            <tr key={rowIndex}>
              {columns.flatMap((column) =>
                column.sides.map((side) => {
                  const cellKey = `${side}-${rowIndex}-${column.key}`;
                  return (
                    <DiffValueCell
                      key={`${column.key}-${side}`}
                      change={change}
                      columnKey={column.key}
                      columnsConfig={columnsConfig}
                      rowIndex={rowIndex}
                      side={side}
                      userHeight={getHeight(cellKey)}
                      onResize={(height) => setHeight(cellKey, height)}
                    />
                  );
                }),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Mirrors the editable table's header: type icon only for configured types.
const ColumnHeaderCell: React.FC<{
  column: DiffColumn;
  columnsConfig: ColumnsConfig;
}> = ({ column, columnsConfig }) => {
  const displayName = getColumnDisplayName(column.key, columnsConfig);
  const displayType = getColumnDisplayType(column.key, columnsConfig);
  const schemaType = displayType === "currency" ? "number" : displayType;
  const typeIcon =
    schemaType && isBaseSchemaType(schemaType)
      ? getSchemaTypeIcon(schemaType)
      : null;

  return (
    <th
      className="border border-gray-100 p-0 text-left"
      colSpan={column.sides.length}
      data-loc={`table-changes-header-${column.key}`}
    >
      <div className="flex h-8 items-center gap-1.5 pr-2 pl-1.5 font-inter-medium-12px text-gray-800">
        {typeIcon && <Icon icon={typeIcon} size="2xs" />}
        <span className="truncate">{displayName}</span>
      </div>
    </th>
  );
};

const SubHeaderCell: React.FC<{ side: DiffSide }> = ({ side }) => (
  <th
    className="border border-gray-100 bg-gray-50 p-0 text-left"
    style={SUB_COLUMN_STYLE}
  >
    <div
      className={twJoin(
        "flex h-8 items-center pr-2 pl-1.5 font-inter-medium-12px",
        side === "before" ? "text-gray-500" : "text-gray-800",
      )}
    >
      {SIDE_LABELS[side]}
    </div>
  </th>
);

// Read-only cells stay gray; added rows tint the after side green, removed
// rows tint the before side red, and in-place edits highlight changed cells
// yellow on the after side.
const getCellBackground = (
  change: TableRowChange,
  columnKey: string,
  side: DiffSide,
): string | false => {
  if (side === "readonly") return "bg-gray-50";
  const kind = getRowChangeKind(change);
  if (kind === "added") return side === "after" && "bg-green-50";
  if (kind === "removed") return side === "before" && "bg-red-50";
  return side === "after" && isCellChanged(change, columnKey) && "bg-yellow-50";
};

const DiffValueCell: React.FC<{
  change: TableRowChange;
  columnKey: string;
  columnsConfig: ColumnsConfig;
  rowIndex: number;
  side: DiffSide;
  userHeight?: number;
  onResize: (height: number) => void;
}> = ({
  change,
  columnKey,
  columnsConfig,
  rowIndex,
  side,
  userHeight,
  onResize,
}) => {
  const isReadOnly = side === "readonly";
  const row = isReadOnly ? (change.after ?? change.before) : change[side];

  return (
    <td
      className={twJoin(
        "border border-gray-100 p-0 align-top",
        getCellBackground(change, columnKey, side),
      )}
      data-loc={`table-changes-${side}-${rowIndex}-${columnKey}`}
      style={SUB_COLUMN_STYLE}
    >
      <div
        className={twJoin(
          "relative flex w-full px-1.5 font-inter-normal-12px contain-inline-size",
          isReadOnly ? "text-gray-500" : "text-gray-800",
          userHeight !== undefined
            ? "items-start"
            : "h-8 items-center overflow-hidden whitespace-nowrap",
        )}
      >
        {row && (
          <DiffCellContent
            columnKey={columnKey}
            columnsConfig={columnsConfig}
            userHeight={userHeight}
            value={row[columnKey]}
            onResize={onResize}
          />
        )}
      </div>
    </td>
  );
};

const DiffCellContent: React.FC<{
  value: unknown;
  columnKey: string;
  columnsConfig: ColumnsConfig;
  userHeight?: number;
  onResize: (height: number) => void;
}> = ({ value, columnKey, columnsConfig, userHeight, onResize }) => {
  const displayType = resolveColumnType(
    columnsConfig?.[columnKey]?.display_config ?? null,
    value,
  );

  if (value != null && isStringCell(value, displayType)) {
    return (
      <ReadModeStringCell
        columnKey={columnKey}
        displayType={displayType}
        userHeight={userHeight}
        value={value}
        onResize={onResize}
      />
    );
  }

  return (
    <TypedCellDisplay
      columnKey={columnKey}
      displayType={displayType}
      value={value}
    />
  );
};
